import { Alert, AlertTitle, Slide, Snackbar, type SnackbarCloseReason, Stack } from '@mui/material';
import { type ForwardedRef, type ReactNode, forwardRef, useState } from 'react';

import { Text } from '../Typography/Text.js';

export const AUTO_HIDE_DURATION = 6000;
const ANIMATION_DURATION = 500;

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  title: string;
  body: ReactNode;
}

export interface ToastProps {
  /**
   * The number of milliseconds to wait before automatically calling the onClose function.
   * Should be a minimum of 2000ms.
   *
   * Passing null will result in the toast staying on the screen until it is manually closed.
   * @default 6000
   */
  autoHideDuration?: number | null;

  /**
   * The message to display.
   */
  message: string | ToastMessage;

  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;

  /**
   * The toast variant to use.
   * @default "info"
   */
  variant?: ToastVariant;
}

/**
 * Toasts serve the purpose of displaying feedback to the user when there has
 * been an update to a state on a page.
 *
 * Mobile toasts have a different style and placement from desktop toasts, so
 * help better support the container spacing on mobile views, and not risk
 * covering a pages call to action. They pop down from behind the top navigation
 * when presented, and return when cleared. They should be sticky with the top
 * navigation, which will ensure they are present when scrolling. They should
 * always be full width, and should auto-clear after 6000ms.
 *
 * Desktop toasts should be placed at the bottom right of the page, and pop out
 * from the right of the page. When cleared, they should clear out and to the
 * right of the page. At the end of their transition, they should be 32px from
 * the bottom right of the page.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1852%3A1827
 */
export const Toast = forwardRef((props: ToastProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { message, onClose, variant = 'info', autoHideDuration = AUTO_HIDE_DURATION } = props;
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Snackbar
      ref={ref}
      open={isOpen}
      autoHideDuration={autoHideDuration}
      onClose={(_event, reason: SnackbarCloseReason) => {
        if (reason === 'clickaway') {
          return;
        }
        setIsOpen(false);
        setTimeout(onClose, ANIMATION_DURATION);
      }}
      slots={{ transition: Slide }}
      slotProps={{ transition: { direction: 'left' } }}
      transitionDuration={ANIMATION_DURATION}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ position: 'static' }}
    >
      <Alert
        severity={variant}
        variant="filled"
        onClose={() => {
          setIsOpen(false);
          setTimeout(onClose, ANIMATION_DURATION);
        }}
        slotProps={{ closeButton: { 'aria-label': 'Close toast' } }}
        sx={{
          '& .MuiAlert-message': {
            whiteSpace: 'pre-wrap',
            alignSelf: 'center',
            maxHeight: 300,
            maxWidth: 400,
            overflow: 'auto',
            // custom scrollbar styles
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            },
          },
        }}
      >
        {typeof message === 'string' ? (
          <Text variant="body2" color="inherit">
            {message}
          </Text>
        ) : (
          <Stack>
            <AlertTitle>{message.title}</AlertTitle>
            <Text variant="body2" color="inherit">
              {message.body}
            </Text>
          </Stack>
        )}
      </Alert>
    </Snackbar>
  );
});

Toast.displayName = 'Toast';
